import { useState, useEffect, useRef, useCallback } from 'react';
import {
  NotebookPen, CalendarDays, Plus, Search, Pin, PinOff,
  Mic, MicOff, Play, Pause, Trash2, Pencil, X, Check,
  ChevronDown, Clock, CheckCircle2, XCircle, AlarmClock,
  FileText, Volume2, Tag, Palette,
} from 'lucide-react';
import {
  apiGetNotes, apiCreateNote, apiUpdateNote, apiDeleteNote, apiTogglePin,
  apiUploadVoiceNote,
  apiGetAppointments, apiCreateAppointment, apiUpdateAppointment,
  apiDeleteAppointment, apiUpdateAppointmentStatus,
} from '../api/client';

/* ─── helpers ─────────────────────────────────────────────────── */
function apiBase() {
  return (
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === 'localhost' ? 'https://localhost:7040' : '')
  );
}
function fullUrl(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${apiBase()}${url}` : url;
}
function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}
function fmtDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  // local datetime-local value
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const NOTE_COLORS = [
  '', '#fef9c3', '#dcfce7', '#dbeafe', '#fce7f3',
  '#ede9fe', '#ffedd5', '#f1f5f9',
];
const CATEGORIES = ['All', 'Work', 'Personal', 'Clients', 'Other'];
const REMINDER_OPTIONS = [
  { label: 'apptReminderNone', value: null },
  { label: 'apptReminder5', value: 5 },
  { label: 'apptReminder10', value: 10 },
  { label: 'apptReminder15', value: 15 },
  { label: 'apptReminder30', value: 30 },
  { label: 'apptReminder60', value: 60 },
  { label: 'apptReminder1440', value: 1440 },
];

/* ─── status badge ─────────────────────────────────────────────── */
function StatusBadge({ status, t }) {
  const map = {
    Upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
    Completed: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    Cancelled: 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400',
  };
  const labels = {
    Upcoming: t('apptStatusUpcoming'),
    Completed: t('apptStatusCompleted'),
    Cancelled: t('apptStatusCancelled'),
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || ''}`}>
      {labels[status] || status}
    </span>
  );
}

/* ─── voice recorder hook ─────────────────────────────────────── */
function useVoiceRecorder() {
  const [state, setState] = useState('idle'); // idle | recording | done
  const [blob, setBlob] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBlob(b);
        setState('done');
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      startTimeRef.current = Date.now();
      setState('recording');
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 500);
    } catch {
      alert('Microphone access denied.');
    }
  }, []);

  const stop = useCallback(() => {
    clearInterval(timerRef.current);
    mediaRef.current?.stop();
  }, []);

  const reset = useCallback(() => {
    setBlob(null);
    setDuration(0);
    setState('idle');
  }, []);

  return { state, blob, duration, start, stop, reset };
}

/* ─── audio player ─────────────────────────────────────────────── */
function AudioPlayer({ src, t }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  useEffect(() => {
    return () => audioRef.current?.pause();
  }, []);
  const toggle = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(src);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };
  return (
    <button
      onClick={toggle}
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
    >
      {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      <Volume2 className="w-4 h-4" />
      <span>{playing ? t('notesRecordPause') : t('notesRecordPlay')}</span>
    </button>
  );
}

/* ─── note sample data ────────────────────────────────────────── */
const NOTE_SAMPLES = [
  {
    title: 'Q3 Project Kickoff',
    content: 'Discuss sprint goals, assign owners, and finalize the roadmap for the upcoming quarter. Review blockers from last sprint and set priorities.',
    category: 'Work',
    color: '#dbeafe',
    tags: 'planning, sprint, roadmap',
    isPinned: true,
  },
  {
    title: 'Book Recommendations',
    content: '1. Atomic Habits — James Clear\n2. Deep Work — Cal Newport\n3. The Pragmatic Programmer — Hunt & Thomas\n4. Designing Data-Intensive Applications — Kleppmann',
    category: 'Personal',
    color: '#dcfce7',
    tags: 'books, reading, self-improvement',
    isPinned: false,
  },
  {
    title: 'Client Meeting Notes',
    content: 'Client requested a redesign of the dashboard with dark mode support. They want weekly progress updates via email. Budget approved for 3 additional team members.',
    category: 'Clients',
    color: '#fce7f3',
    tags: 'client, meeting, design',
    isPinned: false,
  },
  {
    title: 'Ideas to Explore',
    content: '- Build a habit tracker integrated with CoreTask\n- Add AI-generated task descriptions\n- Weekly digest email with team progress\n- Offline mode with sync',
    category: 'Work',
    color: '#ede9fe',
    tags: 'ideas, features, future',
    isPinned: false,
  },
];

/* ═══════════════════════════════════════════════════════════════ */
/*  NOTE MODAL                                                      */
/* ═══════════════════════════════════════════════════════════════ */
function NoteModal({ note, onClose, onSaved, t }) {
  const isEdit = !!note?.id;
  const [type, setType] = useState(note?.type || 'Text');
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [category, setCategory] = useState(note?.category || 'Other');
  const [color, setColor] = useState(note?.color || '');
  const [tags, setTags] = useState(note?.tags || '');
  const [isPinned, setIsPinned] = useState(note?.isPinned || false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [sampleOpen, setSampleOpen] = useState(false);

  const fillSample = (sample) => {
    setType('Text');
    setTitle(sample.title);
    setContent(sample.content);
    setCategory(sample.category);
    setColor(sample.color);
    setTags(sample.tags);
    setIsPinned(sample.isPinned);
    setSampleOpen(false);
    setErr('');
  };

  const recorder = useVoiceRecorder();

  const save = async () => {
    if (!title.trim()) { setErr(t('notesTitleLabel') + ' required'); return; }
    setSaving(true); setErr('');
    try {
      if (type === 'Voice' && !isEdit) {
        if (!recorder.blob) { setErr('Record audio first.'); setSaving(false); return; }
        const file = new File([recorder.blob], 'voice.webm', { type: 'audio/webm' });
        const saved = await apiUploadVoiceNote(file, {
          title: title.trim(), category, duration: recorder.duration,
        });
        onSaved(saved);
      } else {
        const payload = { title: title.trim(), content, category, color, tags, isPinned };
        const saved = isEdit
          ? await apiUpdateNote(note.id, payload)
          : await apiCreateNote(payload);
        onSaved(saved);
      }
      onClose();
    } catch (e) {
      setErr(e.message || 'Error saving note.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="font-semibold text-slate-800 dark:text-white">
            {isEdit ? t('notesEdit') : t('notesNewNote')}
          </h2>
          <div className="flex items-center gap-2">
            {/* Fill sample data — new notes only */}
            {!isEdit && (
              <div className="relative">
                <button
                  onClick={() => setSampleOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {t('fillSampleData')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sampleOpen ? 'rotate-180' : ''}`} />
                </button>
                {sampleOpen && (
                  <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-10 overflow-hidden">
                    {NOTE_SAMPLES.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => fillSample(s)}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                      >
                        <span className="font-medium block truncate">{s.title}</span>
                        <span className="text-xs text-slate-400">{s.category} · {s.tags.split(',')[0].trim()}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* type toggle — only for new notes */}
          {!isEdit && (
            <div className="flex gap-2">
              {['Text', 'Voice'].map((tp) => (
                <button
                  key={tp}
                  onClick={() => setType(tp)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    type === tp
                      ? 'bg-indigo-600 text-white'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  }`}
                >
                  {tp === 'Text' ? <FileText className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  {t(tp === 'Text' ? 'notesTypeText' : 'notesTypeVoice')}
                </button>
              ))}
            </div>
          )}

          {/* title */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('notesTitleLabel')}
            </label>
            <input
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('notesTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* text content */}
          {type === 'Text' && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                {t('notesContentLabel')}
              </label>
              <textarea
                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows={5}
                placeholder={t('notesContentPlaceholder')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </div>
          )}

          {/* voice recorder */}
          {type === 'Voice' && !isEdit && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3">
              {recorder.state === 'idle' && (
                <button
                  onClick={recorder.start}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-pink-500 text-white text-sm font-medium hover:bg-pink-600 transition-colors"
                >
                  <Mic className="w-4 h-4" />
                  {t('notesRecordStart')}
                </button>
              )}
              {recorder.state === 'recording' && (
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 text-red-500 text-sm font-medium animate-pulse">
                    <MicOff className="w-4 h-4" />
                    {t('notesRecording')} {recorder.duration}s
                  </span>
                  <button
                    onClick={recorder.stop}
                    className="px-3 py-1.5 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-sm hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                  >
                    {t('notesRecordStop')}
                  </button>
                </div>
              )}
              {recorder.state === 'done' && recorder.blob && (
                <div className="flex items-center gap-3">
                  <AudioPlayer src={URL.createObjectURL(recorder.blob)} t={t} />
                  <button
                    onClick={recorder.reset}
                    className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                  >
                    {t('notesRecordStart')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* voice note existing audio */}
          {type === 'Voice' && isEdit && note?.audioUrl && (
            <AudioPlayer src={fullUrl(note.audioUrl)} t={t} />
          )}

          {/* category */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('notesCatAll')} / Category
            </label>
            <select
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {['Work', 'Personal', 'Clients', 'Other'].map((c) => (
                <option key={c} value={c}>{t(`notesCat${c}`)}</option>
              ))}
            </select>
          </div>

          {/* color + tags (text notes) */}
          {type === 'Text' && (
            <>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                  <Palette className="w-3.5 h-3.5" />
                  {t('notesColorLabel')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  {NOTE_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      title={c || 'Default'}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${
                        color === c
                          ? 'border-indigo-500 scale-110'
                          : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: c || '#e2e8f0' }}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  <Tag className="w-3.5 h-3.5" />
                  {t('notesTagsLabel')}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('notesTagsPlaceholder')}
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                />
              </div>
            </>
          )}

          {/* pin */}
          <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-slate-700 dark:text-slate-300">
            <input type="checkbox" className="rounded" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
            <Pin className="w-4 h-4" />
            {t('notesPinned')}
          </label>

          {err && <p className="text-red-500 text-sm">{err}</p>}
        </div>

        {/* footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {saving ? '…' : t('notesSave')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── appointment sample data ─────────────────────────────────── */
function buildApptSamples() {
  const now = new Date();
  const at = (daysOffset, hour, min = 0) => {
    const d = new Date(now);
    d.setDate(d.getDate() + daysOffset);
    d.setHours(hour, min, 0, 0);
    return fmtDateInput(d.toISOString());
  };
  return [
    {
      title: 'Team Sprint Review',
      desc: 'Review completed tasks, demo new features, and discuss blockers from the current sprint.',
      date: at(1, 10),
      reminder: 15,
    },
    {
      title: 'Client Design Presentation',
      desc: 'Present the updated dashboard mockups and collect feedback on the new color scheme.',
      date: at(3, 14),
      reminder: 30,
    },
    {
      title: 'One-on-One with Manager',
      desc: 'Weekly check-in to discuss career goals, workload balance, and upcoming priorities.',
      date: at(5, 9, 30),
      reminder: 10,
    },
    {
      title: 'Doctor Appointment',
      desc: 'Annual health check-up at the clinic. Bring insurance card and previous test results.',
      date: at(7, 11),
      reminder: 60,
    },
  ];
}

/* ═══════════════════════════════════════════════════════════════ */
/*  APPOINTMENT MODAL                                              */
/* ═══════════════════════════════════════════════════════════════ */
function AppointmentModal({ appt, notes, onClose, onSaved, t }) {
  const isEdit = !!appt?.id;
  const [title, setTitle] = useState(appt?.title || '');
  const [desc, setDesc] = useState(appt?.description || '');
  const [dateVal, setDateVal] = useState(
    appt?.appointmentDateUtc ? fmtDateInput(appt.appointmentDateUtc) : ''
  );
  const [reminder, setReminder] = useState(appt?.reminderMinutes ?? null);
  const [noteId, setNoteId] = useState(appt?.noteId ?? '');
  const [status, setStatus] = useState(appt?.status || 'Upcoming');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [sampleOpen, setSampleOpen] = useState(false);

  const apptSamples = buildApptSamples();

  const fillSample = (s) => {
    setTitle(s.title);
    setDesc(s.desc);
    setDateVal(s.date);
    setReminder(s.reminder);
    setSampleOpen(false);
    setErr('');
  };

  const save = async () => {
    if (!title.trim()) { setErr(t('apptTitleLabel') + ' required'); return; }
    if (!dateVal) { setErr(t('apptDateLabel') + ' required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = {
        title: title.trim(),
        description: desc.trim() || null,
        appointmentDateUtc: new Date(dateVal).toISOString(),
        reminderMinutes: reminder,
        noteId: noteId ? Number(noteId) : null,
        status,
      };
      const saved = isEdit
        ? await apiUpdateAppointment(appt.id, payload)
        : await apiCreateAppointment(payload);
      onSaved(saved);
      onClose();
    } catch (e) {
      setErr(e.message || 'Error saving appointment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-slate-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="font-semibold text-slate-800 dark:text-white">
            {isEdit ? t('notesEdit') : t('notesNewAppointment')}
          </h2>
          <div className="flex items-center gap-2">
            {!isEdit && (
              <div className="relative">
                <button
                  onClick={() => setSampleOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  {t('fillSampleData')}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${sampleOpen ? 'rotate-180' : ''}`} />
                </button>
                {sampleOpen && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-10 overflow-hidden">
                    {apptSamples.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => fillSample(s)}
                        className="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors border-b border-slate-100 dark:border-slate-700 last:border-0"
                      >
                        <span className="font-medium block truncate">{s.title}</span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <AlarmClock className="w-3 h-3" />
                          {REMINDER_OPTIONS.find((o) => o.value === s.reminder)
                            ? t(REMINDER_OPTIONS.find((o) => o.value === s.reminder).label)
                            : `${s.reminder} min`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* title */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('apptTitleLabel')}
            </label>
            <input
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder={t('apptTitlePlaceholder')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* desc */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('apptDescLabel')}
            </label>
            <textarea
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              rows={3}
              placeholder={t('apptDescPlaceholder')}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </div>

          {/* date */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('apptDateLabel')}
            </label>
            <input
              type="datetime-local"
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={dateVal}
              onChange={(e) => setDateVal(e.target.value)}
            />
          </div>

          {/* reminder */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('apptReminderLabel')}
            </label>
            <select
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={reminder ?? ''}
              onChange={(e) => setReminder(e.target.value === '' ? null : Number(e.target.value))}
            >
              {REMINDER_OPTIONS.map((o) => (
                <option key={o.label} value={o.value ?? ''}>
                  {t(o.label)}
                </option>
              ))}
            </select>
          </div>

          {/* linked note */}
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              {t('apptLinkNoteLabel')}
            </label>
            <select
              className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={noteId}
              onChange={(e) => setNoteId(e.target.value)}
            >
              <option value="">{t('apptLinkNotePlaceholder')}</option>
              {notes.filter((n) => n.type === 'Text').map((n) => (
                <option key={n.id} value={n.id}>{n.title}</option>
              ))}
            </select>
          </div>

          {/* status (edit only) */}
          {isEdit && (
            <div>
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                {t('apptStatusLabel')}
              </label>
              <select
                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 px-3 py-2 text-sm text-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {['Upcoming', 'Completed', 'Cancelled'].map((s) => (
                  <option key={s} value={s}>{t(`apptStatus${s}`)}</option>
                ))}
              </select>
            </div>
          )}

          {err && <p className="text-red-500 text-sm">{err}</p>}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {saving ? '…' : t('notesSave')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  NOTE CARD                                                       */
/* ═══════════════════════════════════════════════════════════════ */
function NoteCard({ note, onEdit, onDelete, onTogglePin, t }) {
  const [delConfirm, setDelConfirm] = useState(false);
  const bg = note.color
    ? `${note.color}22`
    : 'bg-white dark:bg-slate-800';

  return (
    <div
      className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 flex flex-col gap-2 shadow-sm hover:shadow-md transition-shadow relative"
      style={note.color ? { backgroundColor: note.color + '33' } : {}}
    >
      {/* pin badge */}
      {note.isPinned && (
        <span className="absolute top-3 right-3 text-amber-500">
          <Pin className="w-4 h-4" />
        </span>
      )}

      {/* type badge */}
      <div className="flex items-center gap-2">
        {note.type === 'Voice'
          ? <Mic className="w-4 h-4 text-indigo-400" />
          : <FileText className="w-4 h-4 text-slate-400" />}
        <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">
          {t(note.type === 'Voice' ? 'notesTypeVoice' : 'notesTypeText')}
        </span>
        {note.category && note.category !== 'Other' && (
          <span className="ml-auto text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full">
            {t(`notesCat${note.category}`)}
          </span>
        )}
      </div>

      <h3 className="font-semibold text-slate-800 dark:text-white text-sm leading-snug line-clamp-2">
        {note.title}
      </h3>

      {note.type === 'Text' && note.content && (
        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed">
          {note.content}
        </p>
      )}

      {note.type === 'Voice' && note.audioUrl && (
        <AudioPlayer src={fullUrl(note.audioUrl)} t={t} />
      )}

      {note.tags && (
        <div className="flex gap-1 flex-wrap mt-1">
          {note.tags.split(',').map((tag) => tag.trim()).filter(Boolean).map((tag) => (
            <span key={tag} className="text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 px-2 py-0.5 rounded-full">
              #{tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-100 dark:border-slate-700">
        <span className="text-[11px] text-slate-400">{fmtDate(note.updatedUtc)}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onTogglePin(note)}
            title={note.isPinned ? t('notesUnpin') : t('notesPin')}
            className="p-1.5 rounded-lg text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
          >
            {note.isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
          </button>
          <button
            onClick={() => onEdit(note)}
            title={t('notesEdit')}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          {delConfirm ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => { onDelete(note); setDelConfirm(false); }}
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDelConfirm(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDelConfirm(true)}
              title={t('notesDelete')}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  APPOINTMENT CARD                                                */
/* ═══════════════════════════════════════════════════════════════ */
function AppointmentCard({ appt, onEdit, onDelete, onStatusChange, t }) {
  const [delConfirm, setDelConfirm] = useState(false);
  const isPast = new Date(appt.appointmentDateUtc) < new Date();

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-col gap-3 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 dark:text-white text-sm leading-snug line-clamp-2">
            {appt.title}
          </h3>
          {appt.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
              {appt.description}
            </p>
          )}
        </div>
        <StatusBadge status={appt.status} t={t} />
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" />
        <span>{fmtDate(appt.appointmentDateUtc)}</span>
      </div>

      {appt.reminderMinutes && (
        <div className="flex items-center gap-2 text-xs text-indigo-500 dark:text-indigo-400">
          <AlarmClock className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            {REMINDER_OPTIONS.find((o) => o.value === appt.reminderMinutes)
              ? t(REMINDER_OPTIONS.find((o) => o.value === appt.reminderMinutes).label)
              : `${appt.reminderMinutes} min`}
          </span>
        </div>
      )}

      {appt.noteTitle && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">{appt.noteTitle}</span>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700">
        <div className="flex gap-2">
          {appt.status === 'Upcoming' && !isPast && (
            <>
              <button
                onClick={() => onStatusChange(appt, 'Completed')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {t('apptMarkComplete')}
              </button>
              <button
                onClick={() => onStatusChange(appt, 'Cancelled')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                <XCircle className="w-3.5 h-3.5" />
                {t('apptMarkCancel')}
              </button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(appt)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
          {delConfirm ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => { onDelete(appt); setDelConfirm(false); }}
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDelConfirm(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setDelConfirm(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                       */
/* ═══════════════════════════════════════════════════════════════ */
export default function NotesPage({ t, onToast }) {
  const [tab, setTab] = useState('notes'); // 'notes' | 'appointments'

  /* ── notes state ── */
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [noteModal, setNoteModal] = useState(null); // null | 'new' | noteObj

  /* ── appointments state ── */
  const [appts, setAppts] = useState([]);
  const [apptsLoading, setApptsLoading] = useState(true);
  const [apptSearch, setApptSearch] = useState('');
  const [apptStatusFilter, setApptStatusFilter] = useState('All');
  const [apptModal, setApptModal] = useState(null); // null | 'new' | apptObj

  /* ── load notes ── */
  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (catFilter !== 'All') params.category = catFilter;
      if (typeFilter !== 'All') params.type = typeFilter;
      const data = await apiGetNotes(params);
      setNotes(data);
    } catch (e) {
      onToast?.(e.message || 'Failed to load notes.', 'error');
    } finally {
      setNotesLoading(false);
    }
  }, [search, catFilter, typeFilter, onToast]);

  /* ── load appointments ── */
  const loadAppts = useCallback(async () => {
    setApptsLoading(true);
    try {
      const params = {};
      if (apptSearch) params.search = apptSearch;
      if (apptStatusFilter !== 'All') params.status = apptStatusFilter;
      const data = await apiGetAppointments(params);
      setAppts(data);
    } catch (e) {
      onToast?.(e.message || 'Failed to load appointments.', 'error');
    } finally {
      setApptsLoading(false);
    }
  }, [apptSearch, apptStatusFilter, onToast]);

  useEffect(() => { loadNotes(); }, [loadNotes]);
  useEffect(() => { loadAppts(); }, [loadAppts]);

  /* ── note actions ── */
  const handleNoteSaved = (saved) => {
    setNotes((prev) => {
      const idx = prev.findIndex((n) => n.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
    onToast?.('Note saved.', 'success');
  };

  const handleDeleteNote = async (note) => {
    try {
      await apiDeleteNote(note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
      onToast?.('Note deleted.', 'success');
    } catch (e) {
      onToast?.(e.message || 'Delete failed.', 'error');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      const res = await apiTogglePin(note.id);
      setNotes((prev) =>
        prev.map((n) => n.id === note.id ? { ...n, isPinned: res.isPinned } : n)
      );
    } catch (e) {
      onToast?.(e.message || 'Pin toggle failed.', 'error');
    }
  };

  /* ── appointment actions ── */
  const handleApptSaved = (saved) => {
    setAppts((prev) => {
      const idx = prev.findIndex((a) => a.id === saved.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
      return [saved, ...prev];
    });
    onToast?.('Appointment saved.', 'success');
  };

  const handleDeleteAppt = async (appt) => {
    try {
      await apiDeleteAppointment(appt.id);
      setAppts((prev) => prev.filter((a) => a.id !== appt.id));
      onToast?.('Appointment deleted.', 'success');
    } catch (e) {
      onToast?.(e.message || 'Delete failed.', 'error');
    }
  };

  const handleStatusChange = async (appt, newStatus) => {
    try {
      await apiUpdateAppointmentStatus(appt.id, newStatus);
      setAppts((prev) =>
        prev.map((a) => a.id === appt.id ? { ...a, status: newStatus } : a)
      );
      onToast?.(`Marked ${newStatus}.`, 'success');
    } catch (e) {
      onToast?.(e.message || 'Status update failed.', 'error');
    }
  };

  /* ── filtered data ── */
  const filteredNotes = notes; // server-side filtering already applied
  const filteredAppts = appts;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* hero */}
      <div className="bg-gradient-to-br from-[#1A1A1A] to-brand-500 px-6 py-10 md:px-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <NotebookPen className="w-7 h-7 text-white/80" />
            <h1 className="text-2xl font-bold text-white">{t('navNotes')}</h1>
          </div>
          <p className="text-indigo-100 text-sm">
            {t('notesTabNotes')} &amp; {t('notesTabAppointments')}
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        {/* tabs */}
        <div className="flex gap-1 bg-white dark:bg-slate-800 p-1 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 w-fit mb-6">
          {[
            { id: 'notes', label: t('notesTabNotes'), icon: FileText },
            { id: 'appointments', label: t('notesTabAppointments'), icon: CalendarDays },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                tab === id
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* ── NOTES TAB ── */}
        {tab === 'notes' && (
          <>
            {/* toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('notesSearchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* category filter */}
              <div className="flex gap-1 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCatFilter(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      catFilter === c
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {t(`notesCat${c}`)}
                  </button>
                ))}
              </div>

              {/* type filter */}
              <div className="flex gap-1">
                {['All', 'Text', 'Voice'].map((tp) => (
                  <button
                    key={tp}
                    onClick={() => setTypeFilter(tp)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      typeFilter === tp
                        ? 'bg-violet-600 text-white'
                        : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {tp === 'All' ? t('notesCatAll') : t(`notesType${tp}`)}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setNoteModal('new')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow ml-auto"
              >
                <Plus className="w-4 h-4" />
                {t('notesNewNote')}
              </button>
            </div>

            {/* grid */}
            {notesLoading ? (
              <div className="text-center text-slate-400 py-16">{t('uploading')}</div>
            ) : filteredNotes.length === 0 ? (
              <div className="text-center text-slate-400 py-16">
                <NotebookPen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('notesEmpty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onEdit={(n) => setNoteModal(n)}
                    onDelete={handleDeleteNote}
                    onTogglePin={handleTogglePin}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── APPOINTMENTS TAB ── */}
        {tab === 'appointments' && (
          <>
            {/* toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={t('notesSearchPlaceholder')}
                  value={apptSearch}
                  onChange={(e) => setApptSearch(e.target.value)}
                />
              </div>

              {/* status filter */}
              <div className="flex gap-1 flex-wrap">
                {['All', 'Upcoming', 'Completed', 'Cancelled'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setApptStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      apptStatusFilter === s
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    {s === 'All' ? t('notesCatAll') : t(`apptStatus${s}`)}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setApptModal('new')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors shadow ml-auto"
              >
                <Plus className="w-4 h-4" />
                {t('notesNewAppointment')}
              </button>
            </div>

            {/* list */}
            {apptsLoading ? (
              <div className="text-center text-slate-400 py-16">{t('uploading')}</div>
            ) : filteredAppts.length === 0 ? (
              <div className="text-center text-slate-400 py-16">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>{t('apptEmpty')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAppts.map((appt) => (
                  <AppointmentCard
                    key={appt.id}
                    appt={appt}
                    onEdit={(a) => setApptModal(a)}
                    onDelete={handleDeleteAppt}
                    onStatusChange={handleStatusChange}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── modals ── */}
      {noteModal !== null && (
        <NoteModal
          note={noteModal === 'new' ? null : noteModal}
          onClose={() => setNoteModal(null)}
          onSaved={handleNoteSaved}
          t={t}
        />
      )}
      {apptModal !== null && (
        <AppointmentModal
          appt={apptModal === 'new' ? null : apptModal}
          notes={notes}
          onClose={() => setApptModal(null)}
          onSaved={handleApptSaved}
          t={t}
        />
      )}
    </div>
  );
}
